import type { MotionProps } from 'framer-motion';

export const EASE_OUT = [0.23, 1, 0.32, 1] as const;
/** Calm, even deceleration for the intro — no snap at the start, no drawn-out crawl at the end */
export const EASE_SOFT = [0.33, 1, 0.68, 1] as const;

/**
 * Page-load intro: "coming into focus" (~2.6s).
 * Nothing slides out of masks or tilts — everything is already in place and simply resolves,
 * like a camera pulling focus: blur dissolves, opacity rises, a hair of scale settles.
 *  1. the giant wordmark breathes in first, letters drawing together
 *  2. the logo resolves at the top
 *  3. the statement lines follow, one after the other
 *  4. the nav links settle in
 *  5. the showreel eases up from the bottom edge; the drone flies in after
 */
export const INTRO = {
  wordmark: 0.15,
  logo: 0.6,
  statement: 0.85,
  /** Per statement line */
  line: 0.2,
  links: 1.15,
  /** Per nav link */
  link: 0.09,
  reel: 1.4,
  /** Proof points along the hero's bottom edge — rise in as the reel arrives */
  stats: 1.55,
  /** Per stat */
  stat: 0.14,
};

/** Line reveal: rises up out of its own baseline (parent clips it) while coming into focus. */
export function lineRise(delay: number, reduce: boolean | null): MotionProps {
  if (reduce) return {};
  return {
    initial: { y: '110%', opacity: 0, filter: 'blur(6px)' },
    animate: { y: '0%', opacity: 1, filter: 'blur(0px)' },
    transition: {
      y: { duration: 1.1, ease: EASE_SOFT, delay },
      opacity: { duration: 0.8, ease: EASE_SOFT, delay },
      filter: { duration: 1, ease: EASE_SOFT, delay },
    },
  };
}

/** Generic focus-in: blur → sharp, transparent → solid, settling a few px upward. */
export function focusIn(delay: number, reduce: boolean | null, opts: { y?: number; blur?: number; duration?: number } = {}): MotionProps {
  if (reduce) return {};
  const { y = 10, blur = 10, duration = 1.4 } = opts;
  return {
    initial: { opacity: 0, y, filter: `blur(${blur}px)` },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: {
      opacity: { duration: duration * 0.8, ease: EASE_SOFT, delay },
      filter: { duration, ease: EASE_SOFT, delay },
      y: { duration: duration * 1.1, ease: EASE_SOFT, delay },
    },
  };
}

/**
 * Scroll version of the hero's focus-in: the element resolves from blur the first time it scrolls into view.
 * Use one per line (with a small stagger via `delay`) for multi-line headings.
 */
export function focusInView(
  delay: number,
  reduce: boolean | null,
  opts: { y?: number; blur?: number; duration?: number; amount?: number; margin?: string } = {},
): MotionProps {
  if (reduce) return {};
  const { y = 10, blur = 10, duration = 1.4, amount = 0.6, margin } = opts;
  return {
    initial: { opacity: 0, y, filter: `blur(${blur}px)` },
    whileInView: { opacity: 1, y: 0, filter: 'blur(0px)' },
    // `margin` shrinks the trigger area (e.g. '0px 0px -20% 0px' = only once it's 20% above the bottom edge)
    viewport: { once: true, amount, margin },
    transition: {
      opacity: { duration: duration * 0.8, ease: EASE_SOFT, delay },
      filter: { duration, ease: EASE_SOFT, delay },
      y: { duration: duration * 1.1, ease: EASE_SOFT, delay },
    },
  };
}

/** Kept for other callers: soft fade + rise */
export function rise(delay: number, reduce: boolean | null, distance = 12): MotionProps {
  return focusIn(delay, reduce, { y: distance, blur: 6, duration: 1.2 });
}

/** Logo: resolves from soft focus while settling from a hair larger. */
export function logoFocus(delay: number, reduce: boolean | null): MotionProps {
  if (reduce) return {};
  return {
    initial: { opacity: 0, scale: 1.06, filter: 'blur(12px)' },
    animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
    transition: {
      opacity: { duration: 1.2, ease: EASE_SOFT, delay },
      filter: { duration: 1.5, ease: EASE_SOFT, delay },
      scale: { duration: 1.7, ease: EASE_SOFT, delay },
    },
  };
}

/**
 * Giant wordmark: a soft-edged fade that sweeps in from left to right.
 * A gradient mask 3× the width slides across, so letters fade in one after another.
 */
const SWEEP_MASK = 'linear-gradient(to right, #000 33%, transparent 66%)';
export function wordmarkFocus(delay: number, reduce: boolean | null): MotionProps {
  if (reduce) return {};
  return {
    style: {
      WebkitMaskImage: SWEEP_MASK,
      maskImage: SWEEP_MASK,
      WebkitMaskSize: '300% 100%',
      maskSize: '300% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    },
    initial: { WebkitMaskPosition: '100% 0%', maskPosition: '100% 0%' },
    animate: { WebkitMaskPosition: '0% 0%', maskPosition: '0% 0%' },
    transition: { duration: 1.8, ease: [0.45, 0, 0.25, 1], delay },
  };
}

/** Showreel: glides up from below the fold into its resting place. */
export function reelRise(delay: number, reduce: boolean | null): MotionProps {
  if (reduce) return {};
  return {
    initial: { y: 180, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: {
      y: { duration: 1.5, ease: EASE_SOFT, delay },
      opacity: { duration: 0.9, ease: EASE_SOFT, delay },
    },
  };
}
